import { useState } from "react";
import { SimpleCalculationEditor } from "./SimpleCalculationEditor";
import type { Calculation, Condition, Field } from "@proof/core";
import { Select, Button, Control } from "./components";

const literal = (value = ""): Calculation => ({ op: "literal", value });
const operations = {
  literal: "Fixed value",
  field: "Another variable",
  concat: "Join text",
  if: "Conditional value",
  replace: "Replace text",
};
function ConditionEditor({
  value,
  fields,
  onChange,
  label,
}: {
  value: Condition;
  fields: Field[];
  onChange: (value: Condition) => void;
  label: string;
}) {
  const group = value.all ? "all" : value.any ? "any" : "field";
  return (
    <div>
      <Control label={`${label} match`}>
        <Select
          value={group}
          onChange={(e) =>
            onChange(
              e.target.value === "field"
                ? { field: fields[0].id, op: "notEmpty" }
                : {
                    [e.target.value]: [{ field: fields[0].id, op: "notEmpty" }],
                  },
            )
          }
        >
          <option value="field">One condition</option>
          <option value="all">All conditions</option>
          <option value="any">Any condition</option>
        </Select>
      </Control>
      {group !== "field" ? (
        <>
          {(value.all || value.any || []).map((child, index) => (
            <div
              key={index}
              style={{ paddingLeft: 12, borderLeft: "1px solid #d9e0dc" }}
            >
              <ConditionEditor
                value={child}
                fields={fields}
                label={`${label} ${index + 1}`}
                onChange={(next) =>
                  onChange({
                    [group]: (value.all || value.any || []).map((old, i) =>
                      i === index ? next : old,
                    ),
                  })
                }
              />
              {(value.all || value.any || []).length > 1 && (
                <Button
                  onClick={() =>
                    onChange({
                      [group]: (value.all || value.any || []).filter(
                        (_, i) => i !== index,
                      ),
                    })
                  }
                >
                  Remove condition
                </Button>
              )}
            </div>
          ))}
          {(value.all || value.any || []).length < 20 && (
            <Button
              onClick={() =>
                onChange({
                  [group]: [
                    ...(value.all || value.any || []),
                    { field: fields[0].id, op: "notEmpty" },
                  ],
                })
              }
            >
              Add condition
            </Button>
          )}
        </>
      ) : (
        <div className="control-grid">
          <Control label={`${label} variable`}>
            <Select
              value={value.field}
              onChange={(e) => onChange({ ...value, field: e.target.value })}
            >
              {fields.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </Select>
          </Control>
          <Control label={`${label} comparison`}>
            <Select
              value={value.op}
              onChange={(e) =>
                onChange({ ...value, op: e.target.value as Condition["op"] })
              }
            >
              <option value="eq">Equals</option>
              <option value="neq">Does not equal</option>
              <option value="empty">Is empty</option>
              <option value="notEmpty">Is not empty</option>
              <option value="startsWith">Starts with</option>
              <option value="endsWith">Ends with</option>
              <option value="contains">Contains</option>
              <option value="notContains">Does not contain</option>
              <option value="matches">Matches pattern</option>
              <option value="gt">Greater than</option>
              <option value="lt">Less than</option>
              <option value="in">Is in list</option>
            </Select>
          </Control>
          {!["empty", "notEmpty"].includes(value.op || "") && (
            <Control label={`${label} compared value`}>
              <input
                value={
                  Array.isArray(value.value)
                    ? value.value.join(", ")
                    : String(value.value ?? "")
                }
                onChange={(e) =>
                  onChange({
                    ...value,
                    value:
                      value.op === "in"
                        ? e.target.value.split(",").map((s) => s.trim())
                        : e.target.value,
                  })
                }
              />
            </Control>
          )}
        </div>
      )}
    </div>
  );
}

function AdvancedCalculationEditor({
  value,
  fields,
  onChange,
  label = "Calculation",
  depth = 0,
}: {
  value: Calculation;
  fields: Field[];
  onChange: (value: Calculation) => void;
  label?: string;
  depth?: number;
}) {
  const nested = (
    expression: Calculation,
    update: (value: Calculation) => void,
    name: string,
  ) => (
    <AdvancedCalculationEditor
      value={expression}
      fields={fields}
      onChange={update}
      label={name}
      depth={depth + 1}
    />
  );
  return (
    <section
      aria-label={label}
      style={{
        minWidth: 0,
        padding: depth ? 10 : 0,
        borderLeft: depth ? "1px solid #d9e0dc" : undefined,
      }}
    >
      <Control label={`${label} operation`}>
        <Select
          value={value.op}
          onChange={(e) => {
            const op = e.target.value as Calculation["op"];
            onChange(
              op === "field"
                ? { op, fieldId: fields[0].id }
                : op === "concat"
                  ? { op, parts: [literal(), literal()] }
                  : op === "if"
                    ? {
                        op,
                        when: { field: fields[0].id, op: "notEmpty" },
                        then: literal(),
                        else: literal(),
                      }
                    : op === "replace"
                      ? {
                          op,
                          input: literal(),
                          replacements: [{ find: "Search", replacement: "" }],
                        }
                      : literal(),
            );
          }}
        >
          {Object.entries(operations).map(([op, name]) => (
            <option
              key={op}
              value={op}
              disabled={
                ((op === "field" || op === "if") && !fields.length) ||
                (depth >= 12 && op !== "literal" && op !== "field")
              }
            >
              {name}
            </option>
          ))}
        </Select>
      </Control>
      {value.op === "literal" && (
        <>
          <Control label={`${label} value type`}>
            <Select
              value={value.value === null ? "empty" : typeof value.value}
              onChange={(e) =>
                onChange({
                  op: "literal",
                  value:
                    e.target.value === "number"
                      ? 0
                      : e.target.value === "boolean"
                        ? false
                        : e.target.value === "empty"
                          ? null
                          : "",
                })
              }
            >
              <option value="string">Text</option>
              <option value="number">Number</option>
              <option value="boolean">Yes or no</option>
              <option value="empty">No value</option>
            </Select>
          </Control>
          {typeof value.value === "string" && (
            <Control label={`${label} text`}>
              <textarea
                rows={2}
                value={value.value}
                onChange={(e) =>
                  onChange({ op: "literal", value: e.target.value })
                }
              />
            </Control>
          )}
          {typeof value.value === "number" && (
            <Control label={`${label} number`}>
              <input
                type="number"
                value={value.value}
                onChange={(e) =>
                  onChange({ op: "literal", value: Number(e.target.value) })
                }
              />
            </Control>
          )}
          {typeof value.value === "boolean" && (
            <Control label={`${label} answer`}>
              <Select
                value={String(value.value)}
                onChange={(e) =>
                  onChange({ op: "literal", value: e.target.value === "true" })
                }
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Select>
            </Control>
          )}
        </>
      )}
      {value.op === "field" && (
        <Control label={`${label} variable`}>
          <Select
            value={value.fieldId}
            onChange={(e) => onChange({ ...value, fieldId: e.target.value })}
          >
            {fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </Select>
        </Control>
      )}
      {value.op === "concat" && (
        <>
          {value.parts.map((part, index) => (
            <div key={index}>
              {nested(
                part,
                (next) =>
                  onChange({
                    ...value,
                    parts: value.parts.map((old, i) =>
                      i === index ? next : old,
                    ),
                  }),
                `${label} part ${index + 1}`,
              )}
              <div className="button-row">
                {index > 0 && (
                  <Button
                    onClick={() => {
                      const parts = [...value.parts];
                      [parts[index - 1], parts[index]] = [
                        parts[index],
                        parts[index - 1],
                      ];
                      onChange({ ...value, parts });
                    }}
                  >
                    Move part up
                  </Button>
                )}
                {value.parts.length > 1 && (
                  <Button
                    onClick={() =>
                      onChange({
                        ...value,
                        parts: value.parts.filter((_, i) => i !== index),
                      })
                    }
                  >
                    Remove part
                  </Button>
                )}
              </div>
            </div>
          ))}
          {value.parts.length < 64 && (
            <Button
              onClick={() =>
                onChange({ ...value, parts: [...value.parts, literal()] })
              }
            >
              Add text part
            </Button>
          )}
        </>
      )}
      {value.op === "if" && (
        <>
          <ConditionEditor
            value={value.when}
            fields={fields}
            label={`${label} condition`}
            onChange={(when) => onChange({ ...value, when })}
          />
          <h5>When the condition matches</h5>
          {nested(
            value.then,
            (then) => onChange({ ...value, then }),
            `${label} matching result`,
          )}
          <h5>Otherwise</h5>
          {nested(
            value.else,
            (otherwise) => onChange({ ...value, else: otherwise }),
            `${label} other result`,
          )}
        </>
      )}
      {value.op === "replace" && (
        <>
          {nested(
            value.input,
            (input) => onChange({ ...value, input }),
            `${label} source`,
          )}
          {value.replacements.map((replacement, index) => (
            <div key={`${index}:${replacement.find}`} className="control-grid">
              <Control label={`${label} find ${index + 1}`}>
                <input
                  defaultValue={replacement.find}
                  onBlur={(e) => {
                    if (e.target.value)
                      onChange({
                        ...value,
                        replacements: value.replacements.map((old, i) =>
                          i === index ? { ...old, find: e.target.value } : old,
                        ),
                      });
                    else e.target.value = replacement.find;
                  }}
                />
              </Control>
              <Control label={`${label} replacement ${index + 1}`}>
                <textarea
                  rows={2}
                  value={replacement.replacement}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      replacements: value.replacements.map((old, i) =>
                        i === index
                          ? { ...old, replacement: e.target.value }
                          : old,
                      ),
                    })
                  }
                />
              </Control>
              {value.replacements.length > 1 && (
                <Button
                  onClick={() =>
                    onChange({
                      ...value,
                      replacements: value.replacements.filter(
                        (_, i) => i !== index,
                      ),
                    })
                  }
                >
                  Remove replacement
                </Button>
              )}
            </div>
          ))}
          {value.replacements.length < 64 && (
            <Button
              onClick={() =>
                onChange({
                  ...value,
                  replacements: [
                    ...value.replacements,
                    { find: "Search", replacement: "" },
                  ],
                })
              }
            >
              Add replacement
            </Button>
          )}
        </>
      )}
    </section>
  );
}

export function CalculationEditor(props: {
  value: Calculation;
  fields: Field[];
  onChange: (value: Calculation) => void;
  label?: string;
  depth?: number;
}) {
  const [mode, setMode] = useState<"simple" | "advanced">("simple");
  return (
    <section aria-label={`${props.label || "Calculation"} editor`}>
      <div role="group" aria-label="Rule editor mode" className="button-row">
        <button
          type="button"
          className={`button ${mode === "simple" ? "primary" : ""}`}
          aria-pressed={mode === "simple"}
          onClick={() => setMode("simple")}
        >
          Simple
        </button>
        <button
          type="button"
          className={`button ${mode === "advanced" ? "primary" : ""}`}
          aria-pressed={mode === "advanced"}
          onClick={() => setMode("advanced")}
        >
          Advanced
        </button>
      </div>
      {mode === "simple" ? (
        <SimpleCalculationEditor {...props} />
      ) : (
        <AdvancedCalculationEditor {...props} />
      )}
    </section>
  );
}
